import { HttpError } from "@microbeta/common-frontend";
import {
  GetSignedUrlRequestSchema,
  GetSignedUrlResponseSchema,
} from "@microbeta/common-types";
import * as z from "zod";
import config from "../../config";
import { httpClient } from "../utils/httpClientInstance";

type GetSignedUrlPayload = z.infer<typeof GetSignedUrlRequestSchema>;
type GetSignedUrlResponse = z.infer<typeof GetSignedUrlResponseSchema>;

const BASE_URL = `${config.VITE_CONTENT_SERVICE_URL}/content`;

/**
 * Requests a signed URL from the backend for uploading a file using the generic endpoint.
 * @param payload - The details needed to generate the URL (conceptType, conceptId, filename, contentType).
 * @returns A promise that resolves with the signed URL and destination path.
 */
export const getGenericSignedUploadUrl = async (
  payload: GetSignedUrlPayload
): Promise<GetSignedUrlResponse> => {
  try {
    GetSignedUrlRequestSchema.parse(payload);

    let url: string;
    // Use specific endpoints for different concept types
    if (payload.conceptType === "areas") {
      url = `${BASE_URL}/areas/${payload.conceptId}/upload-url`;
    } else if (payload.conceptType === "models") {
      url = `${BASE_URL}/models/${payload.conceptId}/upload-url`;
    } else {
      // Fallback to generic endpoint for other types
      url = `${BASE_URL}/upload/upload-url`;
    }

    const responseData = await httpClient.post<GetSignedUrlResponse>(
      url,
      payload
    );

    GetSignedUrlResponseSchema.parse(responseData);

    return responseData;
  } catch (error: unknown) {
    console.error("Error getting generic signed upload URL:", error);

    if (error instanceof z.ZodError) {
      throw new Error(`Invalid request/response data: ${error.message}`);
    }
    if (error instanceof HttpError) {
      throw error;
    }
    if (error instanceof Error) {
      throw new Error(
        `Failed to get generic signed upload URL: ${error.message}`
      );
    }
    throw new Error(
      "An unknown error occurred while getting the generic signed upload URL."
    );
  }
};

/**
 * Uploads a file directly to Google Cloud Storage using a signed URL.
 * @param signedUrl - The pre-signed URL obtained from the backend.
 * @param file - The File object to upload.
 * @param contentType - The MIME type of the file (must match what was used to generate the URL).
 * @param onProgress - Optional callback function to track upload progress (receives percentage 0-100).
 * @param metadata - Optional metadata to include as headers (for signed URLs that expect specific headers).
 * @returns A promise that resolves when the upload is complete.
 */
export const uploadFileToGCS = async (
  signedUrl: string,
  file: File,
  contentType: string,
  onProgress?: (percentage: number) => void,
  metadata?: {
    entityType?: string;
    entityId?: string;
    userId?: string;
    uploadTimestamp?: string;
  }
): Promise<void> => {
  return new Promise((resolve, reject) => {
    // console.log(
    //   "Uploading file:",
    //   file.name,
    //   "Type:",
    //   file.type,
    //   "Expected:",
    //   contentType
    // );
    // console.log(`Making PUT request to: ${signedUrl.substring(0, 100)}...`);
    // console.log("File size:", file.size, "bytes");
    // console.log("Metadata being sent:", metadata);

    const xhr = new XMLHttpRequest();

    // Track upload progress
    if (onProgress) {
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percentage = Math.round((event.loaded / event.total) * 100);
          onProgress(percentage);
        }
      });
    }

    // Handle completion
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        console.log(`File ${file.name} uploaded successfully to GCS.`);
        resolve();
      } else {
        console.error("Upload failed. Status:", xhr.status);
        console.error("Response text:", xhr.responseText);

        let message = `Upload failed with status ${xhr.status}: ${xhr.statusText}`;
        if (xhr.responseText) {
          message += ` - Response: ${xhr.responseText.substring(0, 200)}`;
        }
        reject(new Error(message));
      }
    });

    // Handle errors
    xhr.addEventListener("error", () => {
      reject(
        new Error("Network error during upload. Please check your connection.")
      );
    });

    // Handle abort
    xhr.addEventListener("abort", () => {
      reject(new Error("Upload was aborted."));
    });

    // Build headers - include metadata headers if they're provided
    xhr.open("PUT", signedUrl);

    // Add Content-Type header to match what was used to sign the URL
    xhr.setRequestHeader("Content-Type", contentType);

    // Add metadata headers if provided (these are expected by signed URLs with extensionHeaders)
    if (metadata) {
      if (metadata.entityType) {
        xhr.setRequestHeader("x-goog-meta-entity-type", metadata.entityType);
      }
      if (metadata.entityId) {
        xhr.setRequestHeader("x-goog-meta-entity-id", metadata.entityId);
      }
      if (metadata.userId) {
        xhr.setRequestHeader("x-goog-meta-uploaded-by", metadata.userId);
      }
      if (metadata.uploadTimestamp) {
        xhr.setRequestHeader(
          "x-goog-meta-upload-timestamp",
          metadata.uploadTimestamp
        );
      }
    }

    console.log("Request headers set on XHR");

    // Start the upload
    xhr.send(file);
  });
};
